import { defineStore } from "pinia";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from "@/js/firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  arrayUnion
} from "firebase/firestore";
import { ref, readonly } from "vue";
import { logger } from "../services/loggerService";
import { storageUsageService } from "../services/storageUsageService";
import { useStorageSubscriptionStore } from "./storageSubscriptionStore";
import {
  canonicalAssetName,
  getMediaMetadata,
} from "../services/assetUploadService";
import { uploadAsset as bunnyUploadAsset } from "../services/bunnyUploadService";

// Map a bunnyUploadService result into the {bunnyStream, bunnyStorage} pointer
// pair stored on an asset doc — exactly one populated, the other null. Single
// source of truth so the new-upload path and the replace path can't drift in
// which fields they copy (videoId/cdnHostname/etc.).
function bunnyPointersFromResult(bunnyResult) {
  if (bunnyResult.kind === 'video') {
    return {
      bunnyStream: {
        libraryId: bunnyResult.bunnyStream.libraryId,
        videoId: bunnyResult.bunnyStream.videoId,
        collectionId: bunnyResult.bunnyStream.collectionId || null,
        cdnHostname: bunnyResult.bunnyStream.cdnHostname || null,
        status: bunnyResult.bunnyStream.status,
      },
      bunnyStorage: null,
    };
  }
  return {
    bunnyStream: null,
    bunnyStorage: {
      path: bunnyResult.bunnyStorage.path,
      cdnHostname: bunnyResult.bunnyStorage.cdnHostname,
      size: bunnyResult.bunnyStorage.size,
      contentType: bunnyResult.bunnyStorage.contentType,
    },
  };
}

export const useClientAssetsStore = defineStore("clientAssets", () => {
  const items = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const initialized = ref(false);
  let unsubscribe = null; // Store the unsubscribe function
  let currentCompanyId = null; // Track current company ID

  // Navigation context for filtered assets
  const navigationContext = ref({
    filteredAssets: [], // The filtered/searched assets array
    currentIndex: -1,   // Current asset index within the filtered array
    hasContext: false,  // Whether navigation context is available
    filterInfo: null,   // Information about active filters (for breadcrumb/UI)
  });

  // Upload-related state
  const uploadProgress = ref(new Map()); // fileId -> { progress: 0-100, status: 'uploading'|'success'|'error'|'paused', error?: string }
  const uploadTasks = ref(new Map()); // fileId -> uploadTask for pause/resume
  const isUploading = ref(false);
  const currentUploadBucketIndex = ref(-1);
  const bucketUploadStates = ref(new Map()); // bucketId -> { status: 'pending'|'uploading'|'completed'|'failed', hasStarted: boolean }

  async function init(companyId, force = false) {
    if (!companyId) {
      logger.error('[ClientAssets] Initialization attempted without company ID');
      throw new Error('Company ID is required for initialization');
    }

    // If we're already initialized for this company, don't reinitialize unless forced
    if (initialized.value && currentCompanyId === companyId && !force) {
      logger.debug('[ClientAssets] Store already initialized for this company');
      return;
    }

    if (force) {
      logger.info('[ClientAssets] Force initialization requested');
    }

    // Reset if we're switching companies
    if (currentCompanyId !== companyId) {
      logger.info('[ClientAssets] Switching companies, resetting store', {
        from: currentCompanyId,
        to: companyId
      });
      reset();
    }

    logger.info('[ClientAssets] Initializing assets', { companyId });
    loading.value = true;
    error.value = null;
    currentCompanyId = companyId;

    try {
      const assetsRef = collection(db, "assets");
      const q = query(assetsRef, where("company", "==", companyId));
      
      // Clean up any existing subscription
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }

      // Set up real-time listener
      unsubscribe = onSnapshot(q, (snapshot) => {
        const assets = [];
        snapshot.forEach((doc) => {
          assets.push({
            id: doc.id,
            ...doc.data()
          });
        });

        items.value = assets;
        initialized.value = true;
        loading.value = false;
        logger.info('[ClientAssets] Assets initialized successfully', { 
          count: assets.length,
          companyId: currentCompanyId
        });
      }, (err) => {
        // Firestore drops listeners on transient network blips and auto-reconnects —
        // only a real failure (permission-denied etc.) deserves the error log.
        const transient = ['unavailable', 'internal', 'cancelled', 'deadline-exceeded'].includes(err.code);
        logger[transient ? 'warn' : 'error']('[ClientAssets] Error in assets subscription:', {
          error: err.message,
          code: err.code,
          companyId: currentCompanyId
        });
        error.value = err;
        loading.value = false;
      });

    } catch (error) {
      logger.error('[ClientAssets] Failed to initialize assets:', {
        error: error.message,
        companyId
      });
      loading.value = false;
      error.value = error;
      throw error;
    }
  }

  function reset() {
    // Clean up Firebase listener if it exists
    if (unsubscribe) {
      logger.info('[ClientAssets] Unsubscribing from Firebase listener');
      unsubscribe();
      unsubscribe = null;
    }

    items.value = [];
    loading.value = false;
    error.value = null;
    initialized.value = false;
    currentCompanyId = null;
    
    // Reset upload state
    uploadProgress.value.clear();
    uploadTasks.value.clear();
    isUploading.value = false;
    currentUploadBucketIndex.value = -1;
    bucketUploadStates.value.clear();
    
    logger.info('[ClientAssets] Store reset completed');
  }

  // Build asset object for a Bunny-backed upload. Omits Firebase-specific
  // fields (file, url, thumbnail) and includes bunnyStream or bunnyStorage
  // pointers instead, depending on the result from bunnyUploadService.
  function buildBunnyAssetObject(file, bucket, metadata, duration, bunnyResult) {
    const fileNameWithoutExtension = canonicalAssetName(file.name);
    const meta = typeof metadata === 'object' && metadata !== null
      ? metadata
      : { aspectRatio: metadata, width: null, height: null };

    const assetObject = {
      name: fileNameWithoutExtension,
      // Seeded from the source file. For videos, onBunnyStreamReady later
      // reconciles this to Bunny's real `storageSize` (source + MP4 variants
      // + HLS + thumbs). Thumbnail bytes are added by generateBunnyPdfThumbnail.
      size: file.file.size,
      type: file.file.type,
      company: currentCompanyId,
      companyName: bucket.companyName || "",
      project: bucket.settings.projectId || "",
      projectName: bucket.settings.projectName || "",
      deliverableId: bucket.settings.deliverableId || "",
      assetType: file.detectedType,
      tags: bucket.settings.tags || [],
      aspectRatio: meta.aspectRatio,
      width: meta.width || null,
      height: meta.height || null,
      uploadDate: Date.now(),
      uploadedByClient: bucket.settings.uploadedByClient || false,
      uploadedBy: bucket.settings.uploadedBy || "",
      uploadSource: bucket.settings.uploadSource || "admin",
      bucket: bucket.settings.bucket || null,
      storageBackend: 'bunny',
    };

    if (duration !== null && duration !== undefined) {
      assetObject.duration = duration;
    }

    // Fresh doc: write only the populated pointer (no need to seed a null sibling).
    const pointers = bunnyPointersFromResult(bunnyResult);
    if (pointers.bunnyStream) assetObject.bunnyStream = pointers.bunnyStream;
    if (pointers.bunnyStorage) assetObject.bunnyStorage = pointers.bunnyStorage;

    return assetObject;
  }

  /**
   * Update an asset and sync with local state
   * @param {string} assetId - Asset ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} - Updated asset
   */
  async function updateAsset(assetId, updateData) {
    if (!assetId) {
      throw new Error('Asset ID is required');
    }

    logger.info('[ClientAssets] Updating asset', {
      assetId,
      updateKeys: Object.keys(updateData)
    });

    try {
      // Update in Firebase
      const assetRef = doc(db, 'assets', assetId);
      const dataWithTimestamp = {
        ...updateData,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(assetRef, dataWithTimestamp);
      
      // Update local state (the real-time listener will also update, but this is for immediate UI feedback)
      const assetIndex = items.value.findIndex(item => item.id === assetId);
      if (assetIndex !== -1) {
        const updatedAsset = { ...items.value[assetIndex], ...dataWithTimestamp };
        items.value[assetIndex] = updatedAsset;
        
        logger.info('[ClientAssets] Asset updated successfully', { assetId });
        return updatedAsset;
      }
      
      logger.warn('[ClientAssets] Asset not found in local state', { assetId });
      return null;
    } catch (error) {
      logger.error('[ClientAssets] Failed to update asset', {
        error: error.message,
        assetId
      });
      throw new Error(`Failed to save asset: ${error.message}`);
    }
  }

  /**
   * Replace asset file while keeping metadata
   * @param {string} assetId - Asset ID
   * @param {File} file - New file to upload
   * @param {Object} updateData - Additional metadata to update
   * @returns {Promise<Object>} - Updated asset
   */
  // Cleanup runs AFTER the new upload so we can compare paths and skip
  // the delete when the new file landed at the same Bunny path as the
  // old one (same assetId + same filename → identical path, since
  // buildBunnyAssetPath is deterministic). Deleting in that case would
  // wipe the freshly-uploaded bytes.
  async function cleanupReplacedAssetArtifacts(currentAsset, companyId, assetId, bunnyResult) {
    // Routed by field presence rather than the storageBackend flag: a couple of
    // docs carry Bunny pointers without it, and the legacy Firebase tail this
    // used to fall through to no longer exists.
    if (isBunnyBackedAsset(currentAsset)) {
      const oldVideoId = currentAsset.bunnyStream?.videoId;
      const newVideoId = bunnyResult.bunnyStream?.videoId;
      const oldPath = currentAsset.bunnyStorage?.path;
      const newPath = bunnyResult.bunnyStorage?.path;

      const safeVideoId = oldVideoId && oldVideoId !== newVideoId ? oldVideoId : undefined;
      const safePath = oldPath && oldPath !== newPath ? oldPath : undefined;
      if (!safeVideoId && !safePath) return;

      try {
        const fn = httpsCallable(getFunctions(), 'deleteBunnyArtifacts');
        await fn({ companyId, videoId: safeVideoId, storagePath: safePath });
        logger.info('[ClientAssets] Old Bunny artifacts deleted', {
          assetId, videoId: safeVideoId, storagePath: safePath,
        });
      } catch (err) {
        logger.warn('[ClientAssets] deleteBunnyArtifacts failed (non-fatal)', {
          assetId, error: err.message,
        });
      }
      return;
    }

    // No Bunny pointer on the previous version — nothing to clean. The legacy
    // Firebase branch (deleting currentAsset.file / .thumbnail / a download-URL
    // derived object) went with the migration.
    logger.warn('[ClientAssets] Replaced asset had no Bunny pointer — nothing to clean up', {
      assetId,
    });
  }

  async function replaceAssetFile(assetId, file, updateData = {}, onProgress = null) {
    if (!assetId || !file) {
      throw new Error('Asset ID and file are required');
    }

    const currentAsset = items.value.find(item => item.id === assetId);
    if (!currentAsset) {
      throw new Error('Asset not found');
    }

    const companyId = currentAsset.company;
    if (!companyId) {
      throw new Error('Asset has no company; cannot resolve Bunny target');
    }

    logger.info('[ClientAssets] Replacing asset file', {
      assetId,
      newFileName: file.name,
      fileSize: file.size,
      oldBackend: currentAsset.storageBackend || 'firebase',
    });

    try {
      // Upload + metadata probe run in parallel — both read from the
      // local File, no shared writes. Cleanup of OLD artifacts is
      // serialized AFTER upload so we can detect path collisions
      // (same assetId + same filename) and skip the delete that would
      // wipe the new bytes.
      const [bunnyResult, mediaMetadata] = await Promise.all([
        bunnyUploadAsset({
          file,
          companyId,
          assetId,
          onProgress: typeof onProgress === 'function' ? onProgress : undefined,
        }),
        getMediaMetadata(file).catch((_error) => {
          logger.warn('[ClientAssets] Failed to get media metadata', {
            error: _error.message,
          });
          return {};
        }),
      ]);

      await cleanupReplacedAssetArtifacts(currentAsset, companyId, assetId, bunnyResult);

      const additionalMetadata = {};
      if (mediaMetadata.aspectRatio) additionalMetadata.aspectRatio = mediaMetadata.aspectRatio;
      if (mediaMetadata.width) additionalMetadata.width = mediaMetadata.width;
      if (mediaMetadata.height) additionalMetadata.height = mediaMetadata.height;
      if (mediaMetadata.duration) additionalMetadata.duration = mediaMetadata.duration;

      // Overwrite bunnyStream/bunnyStorage as whole objects (not nested
      // dot-paths) so cached fields from the previous file — hlsPath,
      // mp4Variants, thumbnailUrl — don't bleed into the replacement.
      const assetUpdateData = {
        ...updateData,
        size: file.size,
        type: file.type,
        storageBackend: 'bunny',
        url: null,
        file: null,
        thumbnail: '',
        thumbnailCacheBuster: null,
        ...additionalMetadata,
        updatedAt: new Date().toISOString(),
      };

      // Update: set both pointers (one null) so any prior pointer is cleared
      // when the replacement file switches storage kind (e.g. video → image).
      Object.assign(assetUpdateData, bunnyPointersFromResult(bunnyResult));

      const assetRef = doc(db, 'assets', assetId);
      await updateDoc(assetRef, assetUpdateData);

      const assetIndex = items.value.findIndex(item => item.id === assetId);
      if (assetIndex !== -1) {
        const updatedAsset = { ...items.value[assetIndex], ...assetUpdateData };
        items.value[assetIndex] = updatedAsset;

        logger.info('[ClientAssets] Asset file replaced', {
          assetId,
          kind: bunnyResult.kind,
        });
        return updatedAsset;
      }

      return null;
    } catch (error) {
      logger.error('[ClientAssets] Failed to replace asset file', {
        error: error.message,
        assetId
      });
      throw new Error(`Failed to replace asset file: ${error.message}`);
    }
  }

  /**
   * Delete an asset and remove from local state
   * @param {string} assetId - Asset ID
   * @returns {Promise<void>}
   */
  async function deleteAsset(assetId) {
    if (!assetId) {
      throw new Error('Asset ID is required');
    }

    logger.info('[ClientAssets] Deleting asset', { assetId });

    try {
      // Get the asset first to capture file size for usage tracking
      const asset = items.value.find(item => item.id === assetId);
      const fileSize = asset?.size || asset?.fileSize || 0;
      const companyId = asset?.company || currentCompanyId;

      // If Bunny-backed, delete the remote media BEFORE the Firestore doc.
      // We do it server-side so Bunny credentials never reach the browser.
      // If this throws, the Firestore doc stays — preferable to orphaning
      // bytes on Bunny that we can no longer locate (the doc is our only
      // index into which Bunny IDs to delete).
      if (isBunnyBackedAsset(asset)) {
        const fn = httpsCallable(getFunctions(), 'deleteBunnyAsset');
        await fn({ assetId });
      }

      // Delete from Firebase
      const assetRef = doc(db, 'assets', assetId);
      await deleteDoc(assetRef);

      // Remove from local state (the real-time listener will also update, but this is for immediate UI feedback)
      const assetIndex = items.value.findIndex(item => item.id === assetId);
      if (assetIndex !== -1) {
        items.value.splice(assetIndex, 1);
        logger.info('[ClientAssets] Asset deleted successfully', { assetId });
      }

      // Decrement storage usage (non-blocking)
      if (fileSize > 0 && companyId) {
        // Optimistically update the local Pinia cache so the banner /
        // upload guard / approve-button flip immediately, even on pages
        // that don't have a live subscribeUsage listener attached.
        try {
          useStorageSubscriptionStore().updateLocalUsage(companyId, fileSize, false);
        } catch (cacheErr) {
          logger.debug('[ClientAssets] Local usage cache update skipped', {
            error: cacheErr.message,
            companyId
          });
        }

        storageUsageService.decrementUsage(companyId, fileSize).catch(err => {
          logger.warn('[ClientAssets] Failed to decrement storage usage', {
            error: err.message,
            assetId,
            fileSize
          });
        });
      }
    } catch (error) {
      logger.error('[ClientAssets] Failed to delete asset', {
        error: error.message,
        assetId
      });
      throw new Error(`Failed to delete asset: ${error.message}`);
    }
  }

  /**
   * Get asset by ID
   * @param {string} assetId - Asset ID
   * @returns {Object|null} - Asset or null if not found
   */
  function getAssetById(assetId) {
    return items.value.find(item => item.id === assetId) || null;
  }

  // True if the asset has any Bunny pointer. Preferred over checking
  // storageBackend === 'bunny' because some older docs carry Bunny fields
  // without the flag (migration artifacts, early Bunny uploads).
  function isBunnyBackedAsset(asset) {
    if (!asset) return false;
    return (
      asset.storageBackend === 'bunny' ||
      !!asset.bunnyStream?.videoId ||
      !!asset.bunnyStorage?.path ||
      !!asset.bunnyThumbnail?.path
    );
  }

  /**
   * Get the full source descriptor for an asset (url + Bunny metadata).
   * Prefer this over getAssetUrl when the caller needs more than a URL
   * string (e.g. the VideoPlayer wants mp4FallbackUrl for HLS resilience,
   * or callers want to distinguish stream-hls / stream-mp4 / storage).
   *
   * Always returns an object: `{ url, kind?, mp4FallbackUrl?, status?, height? }`.
   * Bunny videos that are still transcoding return `{ url: null, status: 'processing' }`.
   *
   * @param {Object} asset
   * @param {string} [intent='playback']  'playback' | 'thumbnail' | 'download'
   * @returns {Promise<Object>}
   */
  async function getAssetSource(asset, intent = 'playback') {
    if (!asset) throw new Error('Asset is required');

    if (isBunnyBackedAsset(asset)) {
      const fn = httpsCallable(getFunctions(), 'getBunnyAssetUrl');
      const { data } = await fn({ assetId: asset.id, intent });
      return data || { url: null };
    }

    // Firebase-backed path — reuse getAssetUrl's logic and wrap into the
    // unified shape. No mp4FallbackUrl or kind on this branch.
    const url = await getAssetUrl(asset);
    return { url, kind: 'firebase' };
  }

  /**
   * Get download URL for an asset
   * @param {Object} asset - Asset object with url, filePath, or file property
   * @returns {Promise<string>} - Download URL
   */
  async function getAssetUrl(asset) {
    if (!asset) {
      throw new Error('Asset is required');
    }

    // Bunny-backed assets: mint a signed URL via the Cloud Function.
    // For videos, transcoding must produce at least one MP4 variant before
    // playback is possible. If none yet, return null — the UI should check
    // asset.bunnyStream.status and render a processing indicator.
    // Routing is by field presence (tolerant of partial data) rather than
    // strictly on storageBackend — some assets have Bunny pointers but no
    // flag (migration artifacts, legacy creates before the flag was added).
    if (isBunnyBackedAsset(asset)) {
      const fn = httpsCallable(getFunctions(), 'getBunnyAssetUrl');
      try {
        const { data } = await fn({ assetId: asset.id, intent: 'playback' });
        return data?.url || null;
      } catch (err) {
        // A callable that timed out / returned `internal` is a transient Cloud
        // Run hiccup, not a bad asset — the alternative is a dead asset page.
        // One retry; anything else (permission-denied, not-found) is real and
        // must surface immediately.
        if (!['functions/deadline-exceeded', 'functions/internal', 'functions/unavailable'].includes(err?.code)) throw err;
        logger.warn('[ClientAssets] Bunny URL callable failed transiently — retrying once', {
          assetId: asset.id,
          code: err.code,
        });
        const { data } = await fn({ assetId: asset.id, intent: 'playback' });
        return data?.url || null;
      }
    }

    // Everything is Bunny-backed. The legacy tail here (asset.url, then
    // getDownloadURL on asset.filePath || asset.file) was removed with the
    // migration — those fields are unset and the objects they named are deleted.
    throw new Error('Asset has no Bunny pointer — cannot resolve a URL');
  }

  /**
   * Get thumbnail URL for an asset
   * @param {Object} asset - Asset object with thumbnail property
   * @returns {Promise<string|null>} - Thumbnail URL or null
   */
  async function getThumbnailUrl(asset) {
    if (!asset) return null;

    // Bunny-backed assets: prefer the pre-signed URL written by
    // cacheBunnyPreviewUrls (30-day TTL) so we skip the Cloud Function
    // round-trip entirely. The cached field is refreshed by the Firestore
    // trigger on any asset doc write, so 99% of reads hit a fresh URL.
    // Falls back to getBunnyAssetUrl for cache misses (legacy docs, expired,
    // or videos still transcoding).
    if (isBunnyBackedAsset(asset)) {
      const nowSec = Math.floor(Date.now() / 1000);
      const streamExpires = asset.bunnyStream?.thumbnailUrlExpires || 0;
      if (asset.bunnyStream?.thumbnailUrl && streamExpires > nowSec) {
        return asset.bunnyStream.thumbnailUrl;
      }
      const storageExpires = asset.bunnyStorage?.thumbnailUrlExpires || 0;
      if (asset.bunnyStorage?.thumbnailUrl && storageExpires > nowSec) {
        return asset.bunnyStorage.thumbnailUrl;
      }
      try {
        const fn = httpsCallable(getFunctions(), 'getBunnyAssetUrl');
        const { data } = await fn({ assetId: asset.id, intent: 'thumbnail' });
        return data?.url || null;
      } catch (err) {
        logger.warn('[ClientAssets] Failed to get Bunny thumbnail URL', {
          assetId: asset.id,
          error: err.message,
        });
        return null;
      }
    }

    // Everything is Bunny-backed; the legacy asset.thumbnail → getDownloadURL
    // tail (with its thumbnailCacheBuster suffix) went with the migration.
    return null;
  }

  /**
   * Delete multiple assets in bulk
   * @param {Array<string>} assetIds - Array of asset IDs
   * @returns {Promise<{success: Array<string>, failed: Array<{id: string, error: string}>}>}
   */
  async function deleteAssetsInBulk(assetIds) {
    if (!Array.isArray(assetIds) || assetIds.length === 0) {
      throw new Error('Asset IDs array is required and cannot be empty');
    }

    logger.info('[ClientAssets] Bulk deleting assets', { 
      assetIds,
      count: assetIds.length
    });

    const results = {
      success: [],
      failed: []
    };

    // Process deletions in parallel with concurrency control
    const concurrency = 5; // Limit concurrent operations
    for (let i = 0; i < assetIds.length; i += concurrency) {
      const batch = assetIds.slice(i, i + concurrency);
      
      const batchPromises = batch.map(async (assetId) => {
        try {
          await deleteAsset(assetId);
          results.success.push(assetId);
          logger.info('[ClientAssets] Asset deleted in bulk operation', { assetId });
        } catch (error) {
          results.failed.push({
            id: assetId,
            error: error.message
          });
          logger.error('[ClientAssets] Failed to delete asset in bulk operation', {
            assetId,
            error: error.message
          });
        }
      });

      await Promise.all(batchPromises);
    }

    logger.info('[ClientAssets] Bulk delete operation completed', {
      successCount: results.success.length,
      failedCount: results.failed.length,
      totalRequested: assetIds.length
    });

    return results;
  }

  /**
   * Add tags to multiple assets in bulk
   * @param {Array<string>} assetIds - Array of asset IDs
   * @param {Array<string>} tagsToAdd - Array of tags to add
   * @returns {Promise<{success: Array<string>, failed: Array<{id: string, error: string}>}>}
   */
  async function addTagsToAssetsInBulk(assetIds, tagsToAdd) {
    if (!Array.isArray(assetIds) || assetIds.length === 0) {
      throw new Error('Asset IDs array is required and cannot be empty');
    }

    if (!Array.isArray(tagsToAdd) || tagsToAdd.length === 0) {
      throw new Error('Tags array is required and cannot be empty');
    }

    logger.info('[ClientAssets] Bulk adding tags to assets', { 
      assetIds,
      tagsToAdd,
      assetCount: assetIds.length,
      tagCount: tagsToAdd.length
    });

    const results = {
      success: [],
      failed: []
    };

    // Process updates in parallel with concurrency control
    const concurrency = 5; // Limit concurrent operations
    for (let i = 0; i < assetIds.length; i += concurrency) {
      const batch = assetIds.slice(i, i + concurrency);
      
      const batchPromises = batch.map(async (assetId) => {
        try {
          // Get current asset
          const asset = getAssetById(assetId);
          if (!asset) {
            throw new Error('Asset not found');
          }

          // Merge tags, removing duplicates (case-insensitive)
          const existingTags = asset.tags || [];
          const normalizedExistingTags = existingTags.map(tag => tag.toLowerCase());
          
          const newTags = tagsToAdd.filter(tag => 
            !normalizedExistingTags.includes(tag.toLowerCase())
          );

          const updatedTags = [...existingTags, ...newTags];

          // Update asset with new tags
          await updateAsset(assetId, { tags: updatedTags });
          
          results.success.push(assetId);
          logger.info('[ClientAssets] Tags added to asset in bulk operation', { 
            assetId,
            newTagsAdded: newTags.length,
            totalTags: updatedTags.length
          });
        } catch (error) {
          results.failed.push({
            id: assetId,
            error: error.message
          });
          logger.error('[ClientAssets] Failed to add tags to asset in bulk operation', {
            assetId,
            error: error.message
          });
        }
      });

      await Promise.all(batchPromises);
    }

    logger.info('[ClientAssets] Bulk tag operation completed', {
      successCount: results.success.length,
      failedCount: results.failed.length,
      totalRequested: assetIds.length,
      tagsAdded: tagsToAdd
    });

    return results;
  }

  /**
   * Regenerate thumbnails for multiple assets in bulk
   * @param {Array<string>} assetIds - Array of asset IDs (max 100)
   * @returns {Promise<{summary: Object, results: Object}>}
   */

  /**
   * Update multiple assets with the same changes
   * @param {Array<string>} assetIds - Array of asset IDs to update
   * @param {Object} changes - Object containing fields to update (assetType, project, projectName)
   * @returns {Promise<{success: Array<string>, failed: Array<{id: string, error: string}>}>}
   */
  async function updateAssetsInBulk(assetIds, changes) {
    if (!Array.isArray(assetIds) || assetIds.length === 0) {
      throw new Error('Asset IDs array is required and cannot be empty');
    }

    if (!changes || Object.keys(changes).length === 0) {
      throw new Error('Changes object is required and cannot be empty');
    }

    logger.info('[ClientAssets] Bulk updating asset details', { 
      assetIds,
      changes,
      assetCount: assetIds.length
    });

    const results = {
      success: [],
      failed: []
    };

    // Validate changes
    const allowedFields = ['assetType', 'project', 'projectName', 'bucket'];
    const updateData = {};
    
    Object.keys(changes).forEach(key => {
      if (allowedFields.includes(key)) {
        updateData[key] = changes[key];
      }
    });

    if (Object.keys(updateData).length === 0) {
      throw new Error('No valid fields to update');
    }

    // Add updatedAt timestamp
    updateData.updatedAt = new Date().toISOString();

    // Process updates in parallel with concurrency control
    const concurrency = 5; // Limit concurrent operations
    for (let i = 0; i < assetIds.length; i += concurrency) {
      const batch = assetIds.slice(i, i + concurrency);
      
      const batchPromises = batch.map(async (assetId) => {
        try {
          // Get current asset to ensure it exists
          const asset = getAssetById(assetId);
          if (!asset) {
            throw new Error('Asset not found');
          }

          // Update the asset
          const assetRef = doc(db, "assets", assetId);
          await updateDoc(assetRef, updateData);
          
          // Update local state
          const assetIndex = items.value.findIndex(a => a.id === assetId);
          if (assetIndex !== -1) {
            items.value[assetIndex] = {
              ...items.value[assetIndex],
              ...updateData
            };
          }
          
          results.success.push(assetId);
          logger.info('[ClientAssets] Asset updated in bulk operation', { assetId, updateData });
        } catch (error) {
          results.failed.push({
            id: assetId,
            error: error.message
          });
          logger.error('[ClientAssets] Failed to update asset in bulk operation', {
            assetId,
            error: error.message,
            updateData
          });
        }
      });

      await Promise.all(batchPromises);
    }

    logger.info('[ClientAssets] Bulk update completed', {
      successCount: results.success.length,
      failedCount: results.failed.length,
      totalRequested: assetIds.length
    });

    return results;
  }

  /**
   * Set navigation context for asset browsing
   * @param {Array} filteredAssets - The filtered assets array
   * @param {string} currentAssetId - Current asset ID to find index for
   * @param {Object} filterInfo - Information about active filters
   */
  function setNavigationContext(filteredAssets, currentAssetId, filterInfo = null) {
    if (!Array.isArray(filteredAssets)) {
      logger.warn('[ClientAssets] Invalid filtered assets array provided');
      return;
    }

    const currentIndex = filteredAssets.findIndex(asset => asset.id === currentAssetId);
    
    navigationContext.value = {
      filteredAssets: [...filteredAssets], // Clone to avoid reactivity issues
      currentIndex,
      hasContext: filteredAssets.length > 0,
      filterInfo: filterInfo ? { ...filterInfo } : null,
    };

    logger.info('[ClientAssets] Navigation context set', {
      totalFiltered: filteredAssets.length,
      currentIndex,
      currentAssetId,
      hasFilters: !!filterInfo
    });
  }

  /**
   * Clear navigation context
   */
  function clearNavigationContext() {
    navigationContext.value = {
      filteredAssets: [],
      currentIndex: -1,
      hasContext: false,
      filterInfo: null,
    };

    logger.info('[ClientAssets] Navigation context cleared');
  }

  /**
   * Get the next asset in the navigation context
   * @returns {Object|null} - Next asset or null if none
   */
  function getNextAsset() {
    if (!navigationContext.value.hasContext || 
        navigationContext.value.currentIndex === -1 ||
        navigationContext.value.currentIndex >= navigationContext.value.filteredAssets.length - 1) {
      return null;
    }

    const nextAsset = navigationContext.value.filteredAssets[navigationContext.value.currentIndex + 1];
    logger.debug('[ClientAssets] Getting next asset', {
      currentIndex: navigationContext.value.currentIndex,
      nextIndex: navigationContext.value.currentIndex + 1,
      nextAssetId: nextAsset?.id
    });

    return nextAsset;
  }

  /**
   * Get the previous asset in the navigation context
   * @returns {Object|null} - Previous asset or null if none
   */
  function getPreviousAsset() {
    if (!navigationContext.value.hasContext || 
        navigationContext.value.currentIndex <= 0) {
      return null;
    }

    const prevAsset = navigationContext.value.filteredAssets[navigationContext.value.currentIndex - 1];
    logger.debug('[ClientAssets] Getting previous asset', {
      currentIndex: navigationContext.value.currentIndex,
      prevIndex: navigationContext.value.currentIndex - 1,
      prevAssetId: prevAsset?.id
    });

    return prevAsset;
  }

  /**
   * Update current asset index in navigation context
   * @param {string} assetId - Asset ID to set as current
   */
  function updateCurrentAssetIndex(assetId) {
    if (!navigationContext.value.hasContext) {
      return;
    }

    const newIndex = navigationContext.value.filteredAssets.findIndex(asset => asset.id === assetId);
    if (newIndex !== -1) {
      navigationContext.value.currentIndex = newIndex;
      logger.debug('[ClientAssets] Updated current asset index', {
        assetId,
        newIndex,
        totalAssets: navigationContext.value.filteredAssets.length
      });
    }
  }

  /**
   * Get navigation info for current context
   * @returns {Object} - Navigation information
   */
  function getNavigationInfo() {
    if (!navigationContext.value.hasContext) {
      return {
        hasContext: false,
        current: 0,
        total: 0,
        hasNext: false,
        hasPrevious: false,
        filterInfo: null
      };
    }

    const current = navigationContext.value.currentIndex + 1; // 1-based for display
    const total = navigationContext.value.filteredAssets.length;
    
    return {
      hasContext: true,
      current,
      total,
      hasNext: navigationContext.value.currentIndex < total - 1,
      hasPrevious: navigationContext.value.currentIndex > 0,
      filterInfo: navigationContext.value.filterInfo
    };
  }

  /**
   * Force refresh thumbnail URLs for specific assets
   * This is needed after thumbnail regeneration to bypass cached URLs
   * @param {Array<string>} assetIds - Array of asset IDs to refresh
   */
  async function refreshThumbnailsForAssets(assetIds) {
    if (!Array.isArray(assetIds) || assetIds.length === 0) {
      logger.warn('[ClientAssets] No asset IDs provided for thumbnail refresh');
      return;
    }

    logger.info('[ClientAssets] Refreshing thumbnail URLs for assets', {
      assetIds,
      count: assetIds.length
    });

    // Update each asset with a new thumbnailUpdatedAt timestamp
    // This will trigger the real-time listener to update the local state
    // and force fresh thumbnail URL retrieval
    const updatePromises = assetIds.map(async (assetId) => {
      try {
        const asset = getAssetById(assetId);
        if (asset) {
          // Update with a timestamp to force cache refresh
          await updateAsset(assetId, {
            thumbnailUpdatedAt: new Date().toISOString(),
            // Add a cache-busting parameter for thumbnail URLs
            thumbnailCacheBuster: Date.now()
          });
          logger.debug('[ClientAssets] Thumbnail refresh metadata updated', { assetId });
        } else {
          logger.warn('[ClientAssets] Asset not found for thumbnail refresh', { assetId });
        }
      } catch (error) {
        logger.error('[ClientAssets] Failed to refresh thumbnail for asset', {
          assetId,
          error: error.message
        });
      }
    });

    try {
      await Promise.all(updatePromises);
      logger.info('[ClientAssets] Thumbnail refresh completed', {
        assetCount: assetIds.length
      });
    } catch (error) {
      logger.error('[ClientAssets] Some thumbnail refreshes failed', {
        error: error.message,
        assetCount: assetIds.length
      });
    }
  }

  // Upload functions
  /**
   * Upload a single file to Firebase Storage and create Firestore document
   * @param {Object} file - File object with file data and metadata
   * @param {Object} bucket - Bucket object with settings
   * @returns {Promise<void>}
   */
  /**
   * Append a screenshot reference to a parent video's `screenshots` array.
   *
   * Called after a screenshot is captured + uploaded from AssetDetail. The
   * array is what powers the "captured frames" strip on the parent's detail
   * page. Failure here is non-fatal — the screenshot asset still exists in
   * the library, only the back-link is missing.
   *
   * @param {Object} params
   * @param {string} params.parentAssetId
   * @param {string} params.screenshotAssetId
   * @param {number} params.timeSeconds  Playhead position when the frame was captured
   */
  async function linkScreenshotToParent({ parentAssetId, screenshotAssetId, timeSeconds }) {
    if (!parentAssetId || !screenshotAssetId) {
      throw new Error('parentAssetId and screenshotAssetId are required');
    }
    const parentRef = doc(db, 'assets', parentAssetId);
    await updateDoc(parentRef, {
      screenshots: arrayUnion({
        assetId: screenshotAssetId,
        timeSeconds: typeof timeSeconds === 'number' ? timeSeconds : 0,
        capturedAt: Date.now(),
      }),
    });
    logger.info('[ClientAssets] Linked screenshot to parent video', {
      parentAssetId, screenshotAssetId, timeSeconds,
    });
  }

  async function handleBunnyUpload(file, bucket) {
    // Pre-reserve a Firestore doc ID so the Bunny Storage path can include it.
    const newAssetRef = doc(collection(db, 'assets'));
    const assetId = newAssetRef.id;

    const metadata_ = await getMediaMetadata(file.file);
    const mediaInfo = { aspectRatio: metadata_.aspectRatio, width: metadata_.width, height: metadata_.height };
    const duration = file.duration ?? metadata_.duration ?? null;

    const bunnyResult = await bunnyUploadAsset({
      file: file.file,
      companyId: currentCompanyId,
      assetId,
      onProgress: (pct) => {
        const current = uploadProgress.value.get(file.id);
        if (current?.status === 'paused') return;
        uploadProgress.value.set(file.id, { progress: pct, status: 'uploading' });
      },
    });

    const assetObject = buildBunnyAssetObject(file, bucket, mediaInfo, duration, bunnyResult);
    await setDoc(newAssetRef, assetObject);

    logger.info('[ClientAssets] Bunny asset saved to Firestore', {
      assetId,
      fileName: file.name,
      kind: bunnyResult.kind,
    });

    const fileSize = file.file?.size || file.size || 0;
    if (fileSize > 0 && assetObject.company) {
      // Optimistically bump the local Pinia usage cache so the over-quota
      // banner / percentage flips immediately, even on pages without a live
      // subscribeUsage listener — mirrors the symmetric decrement in deleteAsset.
      try {
        useStorageSubscriptionStore().updateLocalUsage(assetObject.company, fileSize, true);
      } catch (cacheErr) {
        logger.debug('[ClientAssets] Local usage cache update skipped', {
          error: cacheErr.message, company: assetObject.company,
        });
      }

      storageUsageService.incrementUsage(assetObject.company, fileSize).catch((err) => {
        logger.warn(`[ClientAssets] Failed to increment storage usage for ${file.name}`, {
          error: err.message, fileSize, company: assetObject.company,
        });
      });
    }

    uploadProgress.value.set(file.id, { progress: 100, status: 'success', assetId });
  }

  async function uploadFile(file, bucket) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        // Initialize progress tracking
        uploadProgress.value.set(file.id, {
          progress: 0,
          status: "uploading",
        });

        try {
          await handleBunnyUpload(file, bucket);
          resolve();
        } catch (err) {
          // A dropped connection is the user's network, and an over-limit file
          // is the user's file — neither is a system fault, so neither should
          // page anyone. Both re-log at the bucket level and the UI marks the
          // file failed.
          const isUserFault = err?.message === 'Upload network error'
            || /limited to 32 MB/.test(err?.message || '');
          const level = isUserFault ? 'warn' : 'error';
          logger[level](`[ClientAssets] Bunny upload failed for ${file.name}:`, err);
          uploadProgress.value.set(file.id, {
            progress: 100, status: 'error', error: err.message,
          });
          reject(err);
        }
      } catch (error) {
        logger.error(`[ClientAssets] Unexpected error for ${file.name}:`, error);
        uploadProgress.value.set(file.id, {
          progress: 100,
          status: "error",
          error: error.message,
        });
        reject(error);
      }
    });
  }

  /**
   * Upload all files in a bucket with concurrent limit
   * @param {Object} bucket - Bucket object with files and settings
   * @returns {Promise<void>}
   */
  async function uploadBucket(bucket) {
    // Mark bucket as started and uploading
    bucketUploadStates.value.set(bucket.id, {
      status: "uploading",
      hasStarted: true,
    });

    // Each concurrent upload also runs getMediaMetadata(), which spins up a <video>/<audio>
    // decoder per file. Cap at 2 (well under the ~6-connection-per-host limit) to bound the
    // number of simultaneous decoders and prevent OOM tab crashes on large video batches.
    const CONCURRENT_UPLOAD_LIMIT = 2;

    try {
      // Process files with a constant pool of concurrent uploads
      const files = [...bucket.files];
      const results = [];
      let fileIndex = 0;
      let activeUploads = 0;
      let completedUploads = 0;
      
      logger.info(`[ClientAssets] Starting bucket upload with concurrent pool for ${bucket.name}`, {
        totalFiles: files.length,
        concurrentLimit: CONCURRENT_UPLOAD_LIMIT
      });
      
      await new Promise((resolve) => {
        const startNextUpload = () => {
          // Start uploads until we reach the limit or run out of files
          while (activeUploads < CONCURRENT_UPLOAD_LIMIT && fileIndex < files.length) {
            const file = files[fileIndex];
            const currentIndex = fileIndex;
            fileIndex++;
            activeUploads++;
            
            logger.debug(`[ClientAssets] Starting upload ${currentIndex + 1}/${files.length}`, {
              fileName: file.name,
              activeUploads,
              completedUploads
            });
            
            // Start the upload
            uploadFile(file, bucket)
              .then((result) => {
                results[currentIndex] = result;
                completedUploads++;
                activeUploads--;
                
                logger.debug(`[ClientAssets] Upload completed ${completedUploads}/${files.length}`, {
                  fileName: file.name,
                  activeUploads,
                  remainingFiles: files.length - fileIndex
                });
                
                // Check if all uploads are done
                if (completedUploads === files.length) {
                  resolve();
                } else {
                  // Start next upload to maintain pool
                  startNextUpload();
                }
              })
              .catch((error) => {
                // Duplicate-filename rejections are expected user input, and a
                // dropped connection mid-upload is the user's network — neither
                // is a system error, and the UI already surfaces both. Warn.
                const isDuplicate = error?.message?.startsWith('File already exists');
                const isNetwork = error?.message === 'Upload network error';
                const payload = {
                  fileName: file.name,
                  error: error.message,
                };
                const tag = `[ClientAssets] File upload failed in bucket ${bucket.name}:`;
                if (isDuplicate || isNetwork) {
                  logger.warn(tag, payload);
                } else {
                  logger.error(tag, payload);
                }
                
                results[currentIndex] = error;
                completedUploads++;
                activeUploads--;
                
                // Check if all uploads are done
                if (completedUploads === files.length) {
                  resolve();
                } else {
                  // Start next upload to maintain pool
                  startNextUpload();
                }
              });
          }
        };
        
        // Start initial uploads
        startNextUpload();
      });

      // Check if all files succeeded
      const allSucceeded = bucket.files.every((file) => {
        const progress = getFileUploadProgress(file.id);
        return progress.status === "success";
      });

      // Mark bucket as completed or failed
      bucketUploadStates.value.set(bucket.id, {
        status: allSucceeded ? "completed" : "failed",
        hasStarted: true,
      });

      logger.info(
        `[ClientAssets] Bucket ${bucket.name} finished - Status: ${
          allSucceeded ? "completed" : "failed"
        }`
      );

      // Don't throw error even if some files failed - continue to next bucket
    } catch (error) {
      // This should rarely happen now since we catch individual file errors
      logger.error(
        `[ClientAssets] Unexpected error in bucket ${bucket.name}:`,
        error
      );
      bucketUploadStates.value.set(bucket.id, {
        status: "failed",
        hasStarted: true,
      });
      // Don't throw - continue to next bucket
    }
  }

  /**
   * Start uploading multiple buckets
   * @param {Array} buckets - Array of bucket objects
   * @returns {Promise<Object>} - Upload results summary
   */
  async function startBucketUpload(buckets) {
    if (!Array.isArray(buckets) || buckets.length === 0) {
      throw new Error('Buckets array is required and cannot be empty');
    }

    logger.info('[ClientAssets] Starting bucket upload process', {
      totalBuckets: buckets.length,
      totalFiles: buckets.reduce((total, bucket) => total + bucket.files.length, 0),
      companyId: currentCompanyId,
    });

    isUploading.value = true;
    currentUploadBucketIndex.value = 0;

    // Clear upload state for new upload
    buckets.forEach((bucket) => {
      bucket.files.forEach((file) => {
        uploadProgress.value.delete(file.id);
        uploadTasks.value.delete(file.id);
      });
      bucketUploadStates.value.set(bucket.id, {
        status: "pending",
        hasStarted: false,
      });
    });

    try {
      let completedBuckets = 0;
      let failedBuckets = 0;

      for (let i = 0; i < buckets.length; i++) {
        const bucket = buckets[i];
        currentUploadBucketIndex.value = i;

        logger.info(
          `[ClientAssets] Uploading bucket ${i + 1}/${buckets.length}: ${bucket.name}`,
          {
            fileCount: bucket.files.length,
          }
        );

        await uploadBucket(bucket);

        // Check bucket result
        const bucketState = getBucketUploadState(bucket.id);
        if (bucketState.status === "completed") {
          completedBuckets++;
        } else {
          failedBuckets++;
        }
      }

      const results = {
        total: buckets.length,
        completed: completedBuckets,
        failed: failedBuckets,
        success: failedBuckets === 0
      };

      logger.info('[ClientAssets] Bucket upload process completed', results);
      return results;

    } catch (error) {
      logger.error('[ClientAssets] Bucket upload process failed:', error);
      throw error;
    } finally {
      isUploading.value = false;
      currentUploadBucketIndex.value = -1;
    }
  }

  /**
   * Get upload progress for a file
   * @param {string} fileId - File ID
   * @returns {Object} - Progress object
   */
  function getFileUploadProgress(fileId) {
    return uploadProgress.value.get(fileId) || { progress: 0, status: "pending" };
  }

  /**
   * Get upload state for a bucket
   * @param {string} bucketId - Bucket ID
   * @returns {Object} - Bucket state object
   */
  function getBucketUploadState(bucketId) {
    return bucketUploadStates.value.get(bucketId) || {
      status: "pending",
      hasStarted: false,
    };
  }

  /**
   * Pause file upload
   * @param {string} fileId - File ID
   */
  function pauseFileUpload(fileId) {
    const task = uploadTasks.value.get(fileId);
    if (task && task.pause) {
      task.pause();
      logger.info(`[ClientAssets] Paused upload for file: ${fileId}`);
    }
  }

  /**
   * Resume file upload
   * @param {string} fileId - File ID
   */
  function resumeFileUpload(fileId) {
    const task = uploadTasks.value.get(fileId);
    if (task && task.resume) {
      task.resume();
      logger.info(`[ClientAssets] Resumed upload for file: ${fileId}`);
    }
  }

  /**
   * Retry file upload
   * @param {string} fileId - File ID
   * @param {Object} bucket - Bucket object
   * @returns {Promise<void>}
   */
  async function retryFileUpload(fileId, bucket) {
    // Find the file in the bucket
    const file = bucket.files.find(f => f.id === fileId);
    if (!file) {
      throw new Error(`File not found: ${fileId}`);
    }

    // Reset progress and retry
    uploadProgress.value.set(fileId, {
      progress: 0,
      status: "pending",
    });
    uploadTasks.value.delete(fileId);

    logger.info(`[ClientAssets] Retrying upload for file: ${file.name}`);

    try {
      await uploadFile(file, bucket);
      logger.info(`[ClientAssets] Retry successful for file: ${file.name}`);
      return { success: true };
    } catch (error) {
      logger.error(`[ClientAssets] Retry failed for file ${file.name}:`, error);
      throw error;
    }
  }

  /**
   * Clear upload state for a file
   * @param {string} fileId - File ID
   */
  function clearFileUploadState(fileId) {
    uploadProgress.value.delete(fileId);
    uploadTasks.value.delete(fileId);
  }

  /**
   * Clear upload state for a bucket
   * @param {string} bucketId - Bucket ID
   */
  function clearBucketUploadState(bucketId) {
    bucketUploadStates.value.delete(bucketId);
  }

  /**
   * Create a share link for assets
   * @param {Object} shareData - Share link configuration
   * @param {string} shareData.name - Name for the share
   * @param {string} shareData.description - Description for the share
   * @param {Array<string>} shareData.assetIds - Array of asset IDs to share
   * @param {Object} shareData.settings - Share settings
   * @param {number} shareData.expirationDays - Days until expiration
   * @returns {Promise<Object>} - Share link data with shareUrl
   */
  async function createShareLink(shareData) {
    if (!shareData?.assetIds?.length) {
      throw new Error('No assets selected for sharing');
    }

    logger.info('[ClientAssets] Creating share link', {
      assetCount: shareData.assetIds.length,
      name: shareData.name
    });

    try {
      const functions = getFunctions();
      const createShareLinkFn = httpsCallable(functions, 'createShareLink');

      // Get the company ID from the first asset or from the store's current company
      let targetCompanyId = currentCompanyId;
      if (!targetCompanyId && shareData.assetIds.length > 0) {
        // Get company ID from first asset
        const firstAsset = items.value.find(a => a.id === shareData.assetIds[0]);
        if (firstAsset) {
          targetCompanyId = firstAsset.company;
        }
      }

      if (!targetCompanyId) {
        throw new Error('Unable to determine company ID for share link');
      }

      const result = await createShareLinkFn({
        ...shareData,
        companyId: targetCompanyId
      });

      logger.info('[ClientAssets] Share link created successfully', {
        shareId: result.data.id,
        shareUrl: result.data.shareUrl
      });

      return result.data;
    } catch (error) {
      logger.error('[ClientAssets] Failed to create share link', {
        error: error.message,
        assetCount: shareData.assetIds.length
      });
      throw new Error(`Failed to create share link: ${error.message}`);
    }
  }

  /**
   * Get unique custom bucket names from all assets
   * @returns {Array<string>} - Array of unique bucket names
   */
  function getUniqueBuckets() {
    const buckets = new Set();

    items.value.forEach(asset => {
      if (asset.bucket && typeof asset.bucket === 'string' && asset.bucket.trim()) {
        buckets.add(asset.bucket.trim());
      }
    });

    return Array.from(buckets).sort((a, b) => a.localeCompare(b));
  }

  /**
   * Get tags from the most recently updated assets (unique, up to 10)
   * @returns {Array<string>} - Array of unique tags from recent assets
   */
  function getRecentTags() {
    const sorted = [...items.value]
      .filter(a => a.tags?.length > 0)
      .sort((a, b) => {
        const aTime = a.updatedAt?.toMillis?.() || a.updatedAt?.seconds * 1000 || a.dateCreated?.toMillis?.() || a.dateCreated?.seconds * 1000 || 0;
        const bTime = b.updatedAt?.toMillis?.() || b.updatedAt?.seconds * 1000 || b.dateCreated?.toMillis?.() || b.dateCreated?.seconds * 1000 || 0;
        return bTime - aTime;
      });

    const seen = new Set();
    const result = [];
    for (const asset of sorted) {
      for (const tag of asset.tags) {
        if (tag && typeof tag === 'string' && tag.trim() && !seen.has(tag.trim().toLowerCase())) {
          seen.add(tag.trim().toLowerCase());
          result.push(tag.trim());
          if (result.length >= 10) return result;
        }
      }
    }
    return result;
  }

  /**
   * Get unique tags from all assets for suggestions
   * @returns {Array<string>} - Array of unique tags sorted by frequency
   */
  function getUniqueTags() {
    const tagCounts = new Map();

    items.value.forEach(asset => {
      if (asset.tags && Array.isArray(asset.tags)) {
        asset.tags.forEach(tag => {
          if (tag && typeof tag === 'string' && tag.trim()) {
            const normalizedTag = tag.trim();
            tagCounts.set(normalizedTag, (tagCounts.get(normalizedTag) || 0) + 1);
          }
        });
      }
    });

    // Sort by frequency (most used first), then alphabetically
    return Array.from(tagCounts.entries())
      .sort((a, b) => {
        const countDiff = b[1] - a[1];
        if (countDiff !== 0) return countDiff;
        return a[0].localeCompare(b[0]);
      })
      .map(([tag]) => tag);
  }

  /**
   * Get all assets
   * @returns {Array} - Assets array
   */
  function getFilteredAssets() {
    return items.value;
  }


  /**
   * Update bucket name for multiple assets
   * @param {string} oldBucketName - Current bucket name
   * @param {string} newBucketName - New bucket name (or null to remove)
   * @returns {Promise<{success: Array, failed: Array}>}
   */
  async function renameBucket(oldBucketName, newBucketName) {
    const assetsToUpdate = items.value.filter(a => a.bucket === oldBucketName);

    if (assetsToUpdate.length === 0) {
      logger.warn('[ClientAssets] No assets found with bucket name', { oldBucketName });
      return { success: [], failed: [] };
    }

    logger.info('[ClientAssets] Renaming bucket', {
      oldBucketName,
      newBucketName,
      assetCount: assetsToUpdate.length
    });

    const results = { success: [], failed: [] };

    for (const asset of assetsToUpdate) {
      try {
        await updateAsset(asset.id, { bucket: newBucketName || null });
        results.success.push(asset.id);
      } catch (error) {
        results.failed.push({ id: asset.id, error: error.message });
        logger.error('[ClientAssets] Failed to update asset bucket', {
          assetId: asset.id,
          error: error.message
        });
      }
    }

    logger.info('[ClientAssets] Bucket rename completed', {
      successCount: results.success.length,
      failedCount: results.failed.length
    });

    return results;
  }

  /**
   * Update bucket for multiple assets in bulk
   * @param {Array<string>} assetIds - Asset IDs to update
   * @param {string|null} bucketName - New bucket name (or null to remove)
   * @returns {Promise<{success: Array, failed: Array}>}
   */
  async function bulkUpdateBucket(assetIds, bucketName) {
    if (!Array.isArray(assetIds) || assetIds.length === 0) {
      throw new Error('Asset IDs array is required');
    }

    logger.info('[ClientAssets] Bulk updating bucket', {
      assetCount: assetIds.length,
      bucketName
    });

    const results = { success: [], failed: [] };

    for (const assetId of assetIds) {
      try {
        await updateAsset(assetId, { bucket: bucketName || null });
        results.success.push(assetId);
      } catch (error) {
        results.failed.push({ id: assetId, error: error.message });
      }
    }

    logger.info('[ClientAssets] Bulk bucket update completed', {
      successCount: results.success.length,
      failedCount: results.failed.length
    });

    return results;
  }

  return {
    items,
    loading,
    error,
    initialized,
    navigationContext: readonly(navigationContext), // Make read-only for external access
    // Upload state (read-only)
    uploadProgress: readonly(uploadProgress),
    isUploading: readonly(isUploading),
    currentUploadBucketIndex: readonly(currentUploadBucketIndex),
    // Core methods
    init,
    reset,
    updateAsset,
    replaceAssetFile,
    deleteAsset,
    deleteAssetsInBulk,
    addTagsToAssetsInBulk,
    updateAssetsInBulk,
    refreshThumbnailsForAssets,
    getAssetById,
    getAssetUrl,
    getAssetSource,
    getThumbnailUrl,
    createShareLink,
    getUniqueBuckets,
    getUniqueTags,
    getRecentTags,
    getFilteredAssets,
    renameBucket,
    bulkUpdateBucket,
    // Upload methods
    uploadFile,
    linkScreenshotToParent,
    uploadBucket,
    startBucketUpload,
    getFileUploadProgress,
    getBucketUploadState,
    pauseFileUpload,
    resumeFileUpload,
    retryFileUpload,
    clearFileUploadState,
    clearBucketUploadState,
    // Navigation methods
    setNavigationContext,
    clearNavigationContext,
    getNextAsset,
    getPreviousAsset,
    updateCurrentAssetIndex,
    getNavigationInfo,
  };
});
